import { useCallback, useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import axios from 'axios';
import _ from 'lodash';

import { Icon } from '../shared/Icon';
import { AddProductsButton } from './AddProductsButton';
import { CopyClaimedGiftsButton } from './CopyClaimedGiftsButton';
import CopyProductsModal from './CopyProducts';
import { GiftManagement } from './GiftManagement';
import { UserType } from './UserType';
import { ManagePlaysets, Modal, SpeechBubble } from '../shared';

import { handleError } from '../utils/utils';

import { Purchase } from '../types/accountTypes';
import { Pack } from '../types/packTypes';

export interface ProfilesProp {
    user: UserType;
}

export const UserEditor = function ({ user }: ProfilesProp) {
    const [ownedSKUs, setOwnedSKUs] = useState<string[]>([]);
    const [selectedPacks, setSelectedPacks] = useState<string[]>([]);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [packs, setPacks] = useState<Pack[]>([]);
    const [packIdsToAdd, setPackIdsToAdd] = useState<string[]>([]);

    useEffect(() => {
        const fetchPacksAndEnv = async () => {
            setHasLoaded(true);
            try {
                const response = await axios.get<{ packs: Pack[] }>(
                    '/api/store/v2/packs',
                );
                const packs = response.data.packs;
                setPacks(packs);
            } catch (err) {
                handleError('Failed to fetch packs', err);
                return;
            }
        };

        if (!hasLoaded) fetchPacksAndEnv();
    }, [hasLoaded]);

    const reset = () => {
        setSelectedPacks([]);
        setOwnedSKUs([]);
        setPackIdsToAdd([]);
    };

    const handleOwnedSKUs = useCallback(
        (ownedSKUs: string[]) => {
            // set owned SKUs before marking child packs of bundle as owned
            setOwnedSKUs(ownedSKUs);
            let ownedPacks: string[] = [];
            for (const pack of packs) {
                for (const product of pack.priceLevels) {
                    const productId = product.productId;
                    if (ownedSKUs.indexOf(productId) > -1) {
                        ownedPacks.push(pack.packId);
                        ownedPacks = [...ownedPacks, ...pack.childPacks];
                    }
                }
            }
            // remove duplicate entries
            ownedPacks = [...new Set(ownedPacks)];

            setSelectedPacks(ownedPacks);
        },
        [packs],
    );

    const getPurchases = useCallback(
        (user: UserType) => {
            async function fetchPurchases() {
                try {
                    const res = await axios.get<{ purchases: Purchase[] }>(
                        user.type === 'profileId'
                            ? `/api/accounts/v2/purchases/${user.profileId}`
                            : `/api/accounts/v2/purchases/account/${user.accountId}`,
                    );
                    const purchases = (res.data && res.data.purchases) || [];
                    const ownedSKUs = purchases.map((item) => item.productId);
                    // Reset owned packs
                    setSelectedPacks([]);
                    setOwnedSKUs([]);
                    if (ownedSKUs.length > 0) {
                        handleOwnedSKUs(ownedSKUs);
                    }
                } catch (e) {
                    console.log(`Error fetching purchases`, e);
                    Modal.alert({
                        heading: 'Error getting purchases',
                        text: `Error message ${e}`,
                    });
                }
            }

            fetchPurchases();
        },
        [handleOwnedSKUs],
    );

    useEffect(() => {
        getPurchases(user);
    }, [getPurchases, user]);

    const togglePack = ({ packId }: { packId: string }) => {
        const selected = _.cloneDeep(packIdsToAdd);
        const ix = selected.indexOf(packId);
        if (ix > -1) {
            selected.splice(ix, 1);
        } else {
            selected.push(packId);
        }
        setPackIdsToAdd(selected);
    };

    const selectAllUncheckedPacks = () => {
        const allNotOwnedPacks = packs
            .filter((pack) => !selectedPacks.includes(pack.packId))
            .map((pack) => pack.packId);
        setPackIdsToAdd(allNotOwnedPacks);
    };

    async function onSuccessfullyAddedPacks() {
        setPackIdsToAdd([]);
        getPurchases(user);
    }

    async function deleteAccount(user: UserType) {
        if (user.type === 'accountId') {
            Modal.alert({
                heading: 'Error',
                text: 'There is no delete functionality for users of account type. Aborting.',
            });

            return;
        }
        const isConfirmed = await Modal.confirm({
            heading: 'Delete profile and purchases',
            text: `Really delete all info about selected profile and all its purchases from our system? It can't be undone!`,
            okLabel: 'Yes, do it!',
        });

        if (!isConfirmed) return;
        try {
            await axios.delete(`/api/accounts/v2/account/${user.profileId}`);

            reset();
            Modal.alert({
                heading: 'Account was deleted',
                text: `All info was deleted for profile id "${user.profileId}"`,
            });
        } catch (error) {
            Modal.alert({
                heading: 'Error while deleting account',
                text: `${error}`,
            });
        }
    }

    return (
        <Box sx={{ marginTop: 2 }}>
            <SpeechBubble
                style={{
                    justifyContent: 'left',
                    marginBottom: 20,
                }}
            >
                Looking at{' '}
                {user.type === 'accountId'
                    ? `account ID: ${user.accountId}`
                    : `profile ID: ${user.profileId}`}
            </SpeechBubble>
            <Stack direction="row" spacing={1}>
                <Tooltip
                    title={
                        user.type === 'accountId'
                            ? 'Delete is not support for users of account type'
                            : 'Deletes a user and all of its data'
                    }
                >
                    <Button
                        color="error"
                        variant="outlined"
                        disabled={user.type === 'accountId'}
                        onClick={() => deleteAccount(user)}
                    >
                        Delete account and purchases info
                    </Button>
                </Tooltip>
                <CopyProductsModal
                    sourceUser={user}
                    selectedProductIds={ownedSKUs}
                    allPacksInfo={packs}
                />
                <CopyClaimedGiftsButton sourceUser={user} />
            </Stack>

            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<Icon name="expand_more" />}>
                    <Typography component="span">
                        Products / Packs
                    </Typography>{' '}
                </AccordionSummary>
                <AccordionDetails>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            onClick={() => selectAllUncheckedPacks()}
                        >
                            Select all not owned packs
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setPackIdsToAdd([])}
                        >
                            Unselect all not owned packs
                        </Button>
                    </Stack>
                    <ManagePlaysets
                        title={`Player owned packs (${selectedPacks.length}/${packs.length})`}
                        items={packIdsToAdd}
                        preselectedItems={new Set(selectedPacks)}
                        playsets={packs}
                        togglePlayset={togglePack}
                    />
                    <AddProductsButton
                        user={user}
                        packsToAdd={packs.filter((pack) =>
                            packIdsToAdd.includes(pack.packId),
                        )}
                        onSuccess={onSuccessfullyAddedPacks}
                    />
                </AccordionDetails>
            </Accordion>

            <Accordion>
                <AccordionSummary expandIcon={<Icon name="expand_more" />}>
                    <Typography component="span">Gifts</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <GiftManagement user={user} />
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
